import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function timeToMinutes(str = '') {
  const m = str.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return 9999;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const period = m[3].toUpperCase();
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return h * 60 + min;
}

function MapPinIcon() {
  return (
    <img
      src="/Assets/map-icon.svg"
      alt="Map pin"
      className="w-5 h-5 inline-block"
    />
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={`w-5 h-5 transition-transform duration-300 ${open ? 'rotate-180' : 'rotate-0'}`}
    >
      <path
        fillRule="evenodd"
        d="M12.53 16.28a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 011.06-1.06L12 14.69l6.97-6.97a.75.75 0 111.06 1.06l-7.5 7.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function AccordionRow({ day, venues, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ borderBottom: '1px solid #E5E7EB' }} className="last:border-b-0">
      {/* Day header */}
      <button
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors duration-200 cursor-pointer"
        style={{ backgroundColor: open ? '#FAFAFA' : '#FFFFFF' }}
        aria-expanded={open}
      >
        <span className="font-boxen tracking-widest uppercase text-base sm:text-lg" style={{ color: '#F7941D' }}>
          {day}
        </span>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline font-poppins text-sm" style={{ color: '#888888' }}>
            {venues.length} locations
          </span>
          <span className="sm:hidden font-poppins text-sm" style={{ color: '#888888' }}>
            {venues.length} loc.
          </span>
          <ChevronIcon open={open} />
        </div>
      </button>

      {/* Expandable venue list */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: open ? `${venues.length * 120}px` : '0px' }}
      >
        <ul>
          {venues.map((venue, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-4 px-6 py-3 font-poppins"
              style={{
                backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#F5F5F5',
                borderTop: '1px solid #E5E7EB',
                color: '#1A1A1A',
              }}
            >
              <span className="text-sm font-semibold shrink-0" style={{ minWidth: '5rem', color: '#555' }}>
                {venue.time}
              </span>
              <div className="flex-1 flex flex-col">
                {venue.websiteUrl ? (
                  <a
                    href={venue.websiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-bold hover:underline"
                    style={{ color: '#1A1A1A' }}
                  >
                    {venue.name}
                  </a>
                ) : (
                  <span className="text-sm font-bold">{venue.name}</span>
                )}
                {venue.city && (
                  <span className="sm:hidden text-xs" style={{ color: '#999', fontWeight: 300 }}>
                    {venue.city}
                  </span>
                )}
              </div>
              <span className="text-sm hidden sm:block" style={{ color: '#888', whiteSpace: 'nowrap' }}>
                {venue.city}
              </span>
              {venue.mapsUrl && (
                <a
                  href={venue.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Directions to ${venue.name}`}
                  className="shrink-0 hover:opacity-70 transition-opacity duration-200"
                  style={{ color: '#F7941D' }}
                >
                  <MapPinIcon />
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Loading skeleton ────────────────────────────────────────────────────────────
function ScheduleSkeleton() {
  return (
    <div className="rounded-2xl overflow-hidden animate-pulse" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
      {[1, 2, 3].map(i => (
        <div key={i} className="px-5 py-5" style={{ borderBottom: '1px solid #E5E7EB' }}>
          <div className="h-4 w-28 rounded" style={{ backgroundColor: '#E5E7EB' }} />
        </div>
      ))}
    </div>
  );
}

function Schedule() {
  const [days, setDays]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    async function fetchSchedule() {
      const { data, error: err } = await supabase
        .from('schedule')
        .select('day, time, day_visible, venues(name, city, state, maps_url, website_url)')
        .eq('day_visible', true);

      if (err) {
        setError(true);
        setLoading(false);
        return;
      }

      // Group by day
      const grouped = {};
      (data || []).forEach(entry => {
        if (!entry.venues) return; // skip orphaned entries
        if (!grouped[entry.day]) grouped[entry.day] = [];
        grouped[entry.day].push({
          time:       entry.time,
          name:       entry.venues.name ?? '',
          city:       [entry.venues.city, entry.venues.state].filter(Boolean).join(', '),
          mapsUrl:    entry.venues.maps_url ?? null,
          websiteUrl: entry.venues.website_url ?? null,
        });
      });

      // Sort within each day: by time, then venue name alphabetically
      Object.values(grouped).forEach(list => {
        list.sort((a, b) => {
          const td = timeToMinutes(a.time) - timeToMinutes(b.time);
          return td !== 0 ? td : a.name.localeCompare(b.name);
        });
      });

      // Build ordered list — only days with entries
      const result = DAY_ORDER
        .filter(day => grouped[day]?.length > 0)
        .map(day => ({ day, venues: grouped[day] }));

      setDays(result);
      setLoading(false);
    }

    fetchSchedule();
  }, []);

  return (
    <section
      id="schedule"
      className="w-full py-20 px-6 relative bg-cover bg-center"
      style={{ scrollMarginTop: '120px', backgroundImage: "url('/Assets/Schedule-BG-Image.jpg')" }}
    >
      {/* Gold overlay */}
      <div className="absolute inset-0" style={{ backgroundColor: '#F7941D', opacity: 0.70 }} aria-hidden="true" />

      <div className="relative z-10 max-w-3xl mx-auto">
        {loading && <ScheduleSkeleton />}

        {!loading && error && (
          <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
            <p className="font-poppins text-dark text-sm">
              Unable to load schedule right now. Please check back later.
            </p>
          </div>
        )}

        {!loading && !error && days.length === 0 && (
          <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
            <p className="font-poppins text-dark text-sm">
              No events scheduled at this time. Check back soon!
            </p>
          </div>
        )}

        {!loading && !error && days.length > 0 && (
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
            {days.map(({ day, venues }) => (
              <AccordionRow key={day} day={day} venues={venues} defaultOpen={false} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default Schedule;
